import { useEffect, useState } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BookOpen,
  Video,
  FileText,
  Plus,
  Upload,
  Trophy,
  Users,
  RefreshCw,
  Eye,
  Trash2,
  ExternalLink,
  Download,
  X,
} from "lucide-react";
import { toast } from "sonner";
import QuizManager from "@/components/admin/QuizManager";

const MentorDashboard = () => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [courses, setCourses] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [recordingFile, setRecordingFile] = useState(null);
  const [noteFile, setNoteFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [newRecording, setNewRecording] = useState({
    title: "",
    description: "",
    video_url: "",
    course_id: "",
    duration_minutes: "",
  });

  const [newNote, setNewNote] = useState({
    title: "",
    description: "",
    file_url: "",
    course_id: "",
  });
  const [previewNote, setPreviewNote] = useState(null);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [coursesRes, recordingsRes, notesRes, usersRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/recordings`, { withCredentials: true }),
        axios.get(`${API}/notes`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      setCourses(asArray(coursesRes.data, "courses"));
      setRecordings(asArray(recordingsRes.data, "recordings"));
      setNotes(asArray(notesRes.data, "notes"));
      setUsers(asArray(usersRes.data, "users"));
    } catch (error) {
      console.error("Error fetching data:", error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file, kind) => {
    const data = new FormData();
    data.append("file", file);
    data.append("kind", kind);
    const res = await axios.post(`${API}/uploads`, data, {
      withCredentials: true,
    });
    return res.data.url;
  };

  const createRecording = async () => {
    if (!newRecording.title || (!newRecording.video_url && !recordingFile)) {
      toast.error("Add a title and upload a video or paste a recording link");
      return;
    }
    setUploading(true);
    try {
      const videoUrl = recordingFile
        ? await uploadFile(recordingFile, "recording")
        : newRecording.video_url;
      await axios.post(
        `${API}/recordings`,
        {
          title: newRecording.title.trim(),
          description: newRecording.description || "",
          video_url: videoUrl,
          course_id: newRecording.course_id === "all" ? null : (newRecording.course_id || null),
          duration_minutes: parseInt(newRecording.duration_minutes) || null,
        },
        { withCredentials: true }
      );
      toast.success("Recording uploaded successfully");
      setNewRecording({ title: "", description: "", video_url: "", course_id: "", duration_minutes: "" });
      setRecordingFile(null);
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to upload recording:", error);
      let msg = error?.response?.data?.detail || error?.message || "Failed to upload recording";
      if (error?.response?.status === 413) {
        msg = "Video is too large for direct upload (Server limit 100MB). Please use a link (Google Drive / YouTube / Zoom) or file under 100MB.";
      }
      toast.error(typeof msg === "string" ? msg : "Failed to upload recording");
    } finally {
      setUploading(false);
    }
  };

  const createNote = async () => {
    if (!newNote.title || (!newNote.file_url && !noteFile)) {
      toast.error("Add a title and upload a file or paste a file link");
      return;
    }
    setUploading(true);
    try {
      const fileUrl = noteFile
        ? await uploadFile(noteFile, "note")
        : newNote.file_url;
      await axios.post(
        `${API}/notes`,
        {
          title: newNote.title.trim(),
          description: newNote.description || "",
          course_id: newNote.course_id === "all" ? null : (newNote.course_id || null),
          file_url: fileUrl,
        },
        { withCredentials: true }
      );
      toast.success("Note uploaded successfully");
      setNewNote({ title: "", description: "", file_url: "", course_id: "" });
      setNoteFile(null);
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to upload note:", error);
      let msg = error?.response?.data?.detail || error?.message || "Failed to upload note";
      if (error?.response?.status === 413) {
        msg = "File is too large for upload (Server limit 100MB). Please compress or use a file under 100MB.";
      }
      toast.error(typeof msg === "string" ? msg : "Failed to upload note");
    } finally {
      setUploading(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await axios.delete(`${API}/notes/${noteId}`, { withCredentials: true });
      toast.success("Note deleted successfully");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete note");
    }
  };

  const deleteRecording = async (recordingId) => {
    if (!window.confirm("Are you sure you want to delete this recording?")) return;
    try {
      await axios.delete(`${API}/recordings/${recordingId}`, { withCredentials: true });
      toast.success("Recording deleted successfully");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete recording");
    }
  };

  const openDialog = (type) => {
    setDialogType(type);
    setRecordingFile(null);
    setNoteFile(null);
    setDialogOpen(true);
  };

  const filteredUsers = users.filter((u) => {
    if (!userSearchTerm) return true;
    const term = userSearchTerm.toLowerCase();
    return (
      (u.name && u.name.toLowerCase().includes(term)) ||
      (u.email && u.email.toLowerCase().includes(term)) ||
      (u.phone && String(u.phone).toLowerCase().includes(term)) ||
      (u.user_id && String(u.user_id).toLowerCase().includes(term))
    );
  });

  if (loading) {
    return (
      <DashboardLayout title="Content Management">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Content Management">
      <div className="space-y-6" data-testid="mentor-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card className={`${isLight ? "bg-white border-slate-200/80 text-gray-900 shadow-xs" : "bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]"}`} data-testid="stat-courses">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-outfit`}>{courses.length}</p>
                  <p className={`text-xs ${isLight ? "text-slate-500" : "text-[#64748B]"}`}>Courses</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${isLight ? "bg-white border-slate-200/80 text-gray-900 shadow-xs" : "bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]"}`} data-testid="stat-recordings">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/60 dark:text-teal-400 dark:border-teal-800 flex items-center justify-center">
                  <Video className="w-5 h-5 text-teal-600 dark:text-teal-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-outfit`}>{recordings.length}</p>
                  <p className={`text-xs ${isLight ? "text-slate-500" : "text-[#64748B]"}`}>Recordings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${isLight ? "bg-white border-slate-200/80 text-gray-900 shadow-xs" : "bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]"}`} data-testid="stat-notes">
            <CardContent className="p-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/60 dark:text-blue-400 dark:border-blue-800 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-outfit`}>{notes.length}</p>
                <p className={`text-xs ${isLight ? "text-slate-500" : "text-[#64748B]"}`}>Notes</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="recordings" className="space-y-4">
          <TabsList className={`${isLight ? "bg-white border-slate-200/80 text-gray-900 shadow-xs" : "bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]"} border`}>
            <TabsTrigger value="recordings" data-testid="tab-recordings">Recordings</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            <TabsTrigger value="quizzes" data-testid="tab-quizzes" className="flex items-center gap-1">
              <Trophy className="w-3.5 h-3.5" /> Quizzes
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Users
            </TabsTrigger>
          </TabsList>

          {/* Recordings Tab */}
          <TabsContent value="recordings">
            <Card className={`${isLight ? "bg-white border-slate-200/80 text-gray-900 shadow-xs" : "bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]"}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-outfit text-base font-semibold`}>Recordings</CardTitle>
                <Button
                  onClick={() => openDialog("recording")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors h-9 px-3.5"
                  data-testid="add-recording-btn"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Recording
                </Button>
              </CardHeader>
              <CardContent>
                {recordings.length === 0 ? (
                  <p className={`text-center ${isLight ? "text-slate-500" : "text-[#64748B]"} py-8 text-sm`}>No recordings yet</p>
                ) : (
                  <div className="space-y-3">
                    {recordings.map((rec) => (
                      <div
                        key={rec.recording_id}
                        className={`flex items-center gap-4 p-4 ${isLight ? "bg-slate-50 border border-slate-100" : "bg-gray-50 dark:bg-[#0A0E17]"} rounded-xl`}
                        data-testid={`recording-item-${rec.recording_id}`}
                      >
                        <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/60 dark:text-teal-400 dark:border-teal-800 flex items-center justify-center shrink-0">
                          <Video className="w-5 h-5 text-teal-600 dark:text-teal-400" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-medium text-sm truncate`}>{rec.title}</p>
                          <p className={`text-xs ${isLight ? "text-slate-500" : "text-[#64748B]"}`}>{rec.duration_minutes || 0} min</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {rec.video_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(rec.video_url, "_blank", "noopener,noreferrer")}
                              className="h-8 px-2.5 text-xs text-gray-700 dark:text-gray-300 border-gray-200 dark:border-white/10"
                              title="Watch video"
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" /> Watch
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteRecording(rec.recording_id)}
                            className="h-8 w-8 p-0 text-gray-400 hover:text-rose-500 hover:bg-rose-500/10"
                            title="Delete recording"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <Card className={`${isLight ? "bg-white border-slate-200/80 text-gray-900 shadow-xs" : "bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]"}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-outfit text-base font-semibold`}>Notes</CardTitle>
                <Button
                  onClick={() => openDialog("note")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors h-9 px-3.5"
                  data-testid="add-note-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </CardHeader>
              <CardContent>
                {notes.length === 0 ? (
                  <p className={`text-center ${isLight ? "text-gray-500" : "text-[#64748B]"} py-8`}>No notes yet</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div
                        key={note.note_id}
                        className={`flex items-center gap-4 p-4 ${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} rounded-lg`}
                        data-testid={`note-item-${note.note_id}`}
                      >
                        <div className="w-10 h-10 rounded bg-[#2563EB]/20 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-medium truncate`}>{note.title}</p>
                            {(!note.course_id || note.is_all_students || note.course_id === "all") ? (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shrink-0">All Students</span>
                            ) : (
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shrink-0">
                                {courses.find(c => c.course_id === note.course_id)?.title || "Course"}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${isLight ? "text-gray-500" : "text-[#64748B]"}`}>{note.download_count || 0} downloads</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => setPreviewNote(note)}
                            className="h-8 px-2.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                            title="Read note on dashboard"
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                          </Button>
                          {note.file_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(note.file_url, "_blank", "noopener,noreferrer")}
                              className="h-8 w-8 p-0 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-white/10"
                              title="Download file"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteNote(note.note_id)}
                            className="h-8 w-8 p-0 text-gray-400 hover:text-rose-500 hover:bg-rose-500/10"
                            title="Delete note"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quizzes Tab */}
          <TabsContent value="quizzes">
            <Card className={`${isLight ? "bg-white border-gray-200 text-gray-900 shadow-sm" : "bg-white dark:bg-[#131B2C] "} ${isLight ? "" : "border-gray-200 dark:border-[#1E293B]"}`}>
              <CardContent className="p-6">
                <QuizManager />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className={`${isLight ? "bg-white border-gray-200 text-gray-900 shadow-sm" : "bg-white dark:bg-[#131B2C] "} ${isLight ? "" : "border-gray-200 dark:border-[#1E293B]"}`} data-testid="users-table">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} font-outfit`}>User Directory</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Search ID, name, email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className={`w-48 lg:w-64 bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-[#1E293B] ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} text-sm`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchData}
                    className={`text-[#A1A1AA] hover:${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"}`}
                    data-testid="refresh-users-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                <div className="overflow-x-auto w-full">
                  <Table className="min-w-[650px]">
                    <TableHeader>
                      <TableRow className="border-gray-200 dark:border-[#1E293B] hover:bg-transparent">
                        <TableHead className="text-gray-500 dark:text-[#A1A1AA]">ID</TableHead>
                        <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Name</TableHead>
                        <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Email</TableHead>
                        <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Phone</TableHead>
                        <TableHead className="text-gray-500 dark:text-[#A1A1AA]">City</TableHead>
                        <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Role</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((u) => (
                        <TableRow
                          key={u.user_id}
                          className="border-gray-200 dark:border-[#1E293B] hover:bg-gray-50 dark:bg-white/[0.03]"
                          data-testid={`user-row-${u.user_id}`}
                        >
                          <TableCell className="font-mono text-xs text-gray-400 dark:text-[#52525B]">{u.user_id}</TableCell>
                          <TableCell className={`${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"}`}>{u.name}</TableCell>
                          <TableCell className="text-gray-500 dark:text-[#A1A1AA]">{u.email}</TableCell>
                          <TableCell className="text-gray-500 dark:text-[#A1A1AA]">{u.phone || "-"}</TableCell>
                          <TableCell className="text-gray-500 dark:text-[#A1A1AA]">{u.city || "-"}</TableCell>
                          <TableCell>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 text-xs rounded-full capitalize font-medium">
                              {u.role}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className={`${isLight ? "bg-white border-gray-200 text-gray-900 shadow-sm" : "bg-white dark:bg-[#131B2C] "} ${isLight ? "" : "border-gray-200 dark:border-[#1E293B]"} `}>
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {dialogType === "recording" ? "Add Recording" : "Add Note"}
              </DialogTitle>
            </DialogHeader>

            {dialogType === "recording" ? (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Title *</Label>
                  <Input
                    value={newRecording.title}
                    onChange={(e) => setNewRecording({ ...newRecording, title: e.target.value })}
                    placeholder="Recording title"
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                    data-testid="recording-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Upload Video</Label>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={(e) => setRecordingFile(e.target.files?.[0] || null)}
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white file:text-emerald-600 dark:file:text-emerald-400`}
                    data-testid="recording-file-input"
                  />
                  {recordingFile && <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{recordingFile.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Or Recording URL</Label>
                  <Input
                    value={newRecording.video_url}
                    onChange={(e) => setNewRecording({ ...newRecording, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=... or Zoom recording link"
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                    data-testid="recording-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Description</Label>
                  <Textarea
                    value={newRecording.description}
                    onChange={(e) => setNewRecording({ ...newRecording, description: e.target.value })}
                    placeholder="Brief description"
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                    data-testid="recording-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500 dark:text-[#94A3B8]">Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={newRecording.duration_minutes}
                      onChange={(e) => setNewRecording({ ...newRecording, duration_minutes: e.target.value })}
                      placeholder="30"
                      className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                      data-testid="recording-duration-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500 dark:text-[#94A3B8]">Course</Label>
                    <Select
                      value={newRecording.course_id}
                      onValueChange={(value) => setNewRecording({ ...newRecording, course_id: value })}
                    >
                      <SelectTrigger className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}>
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent className={`${isLight ? "bg-white border-gray-200 text-gray-900 shadow-sm" : "bg-white dark:bg-[#131B2C] "} ${isLight ? "" : "border-gray-200 dark:border-[#1E293B]"}`}>
                        <SelectItem value="all" className="font-semibold text-emerald-600 dark:text-emerald-400">
                          🌟 All Students (Everyone / General)
                        </SelectItem>
                        {courses.map((course) => (
                          <SelectItem key={course.course_id} value={course.course_id}>
                            {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={createRecording}
                  disabled={uploading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors h-10"
                  data-testid="submit-recording-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload Recording"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Title *</Label>
                  <Input
                    value={newNote.title}
                    onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                    placeholder="Note title"
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                    data-testid="note-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Upload File</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => setNoteFile(e.target.files?.[0] || null)}
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white file:text-emerald-600`}
                    data-testid="note-file-input"
                  />
                  {noteFile && <p className="text-xs text-emerald-600 font-medium">{noteFile.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Or File URL</Label>
                  <Input
                    value={newNote.file_url}
                    onChange={(e) => setNewNote({ ...newNote, file_url: e.target.value })}
                    placeholder="https://drive.google.com/..."
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                    data-testid="note-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Description</Label>
                  <Textarea
                    value={newNote.description}
                    onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                    placeholder="Brief description"
                    className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}
                    data-testid="note-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Course</Label>
                  <Select
                    value={newNote.course_id}
                    onValueChange={(value) => setNewNote({ ...newNote, course_id: value })}
                  >
                    <SelectTrigger className={`${isLight ? "bg-gray-50 border border-gray-100" : "bg-gray-50 dark:bg-[#0A0E17]"} border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white`}>
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent className={`${isLight ? "bg-white border-gray-200 text-gray-900 shadow-sm" : "bg-white dark:bg-[#131B2C] "} ${isLight ? "" : "border-gray-200 dark:border-[#1E293B]"}`}>
                      <SelectItem value="all" className="font-semibold text-emerald-600 dark:text-emerald-400">
                        🌟 All Students (Everyone / General)
                      </SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.course_id} value={course.course_id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={createNote}
                  disabled={uploading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors h-10"
                  data-testid="submit-note-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload Note"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Note Preview Modal for Mentor */}
        {previewNote && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in duration-200"
            onClick={() => setPreviewNote(null)}
          >
            <div
              className="relative flex flex-col w-full max-w-5xl h-[88vh] max-h-[920px] bg-white dark:bg-[#0D1117] rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 dark:border-white/10 bg-gray-50/80 dark:bg-white/[0.02]">
                <div className="flex items-center gap-3 min-w-0 pr-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-[#00FF88] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-outfit font-bold text-gray-900 dark:text-white text-base truncate">
                      {previewNote.title}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {previewNote.file_url || "Uploaded note"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(previewNote.file_url, "_blank")}
                    className="h-8 px-3 text-xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> New Tab
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPreviewNote(null)}
                    className="h-8 w-8 p-0 text-gray-500 hover:text-rose-600"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 w-full h-full bg-[#1e1e1e]">
                <iframe
                  src={previewNote.file_url}
                  title={previewNote.title}
                  className="w-full h-full border-0 bg-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MentorDashboard;
